<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import AccountScheduledMessagesAPI from '../../../../api/accountScheduledMessages';
import ConversationApi from '../../../../api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ScheduleMessageModal from 'dashboard/components/widgets/conversation/bubble/ScheduleMessageModal.vue';

const emit = defineEmits(['switchView']);
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const loading = ref(true);
const scheduledMessages = ref([]);
const expandedIds = ref([]);
const statusFilter = ref('all');
const inboxFilter = ref('all');
const showDeleteModal = ref(false);
const messageToDelete = ref(null);
const searchQuery = ref('');
const showSearchInput = ref(false);
const showScheduleModal = ref(false);

const handleSearch = () => {
  showSearchInput.value = !showSearchInput.value;
  if (showSearchInput.value) {
    nextTick(() => document.getElementById('scheduled-messages-search')?.focus());
  }
};

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const accountId = computed(() => store.getters.getCurrentAccountId);

const filteredMessages = computed(() => {
  let list = scheduledMessages.value;
  if (statusFilter.value !== 'all') {
    list = list.filter(m => m.status === statusFilter.value);
  }
  if (inboxFilter.value !== 'all') {
    list = list.filter(m => m.inbox_id === Number(inboxFilter.value));
  }
  const q = searchQuery.value?.trim().toLowerCase();
  if (!q) return list;
  return list.filter(m => {
    const title = (m.title || '').toLowerCase();
    const message = (m.message || '').toLowerCase();
    const contact = ((m.contact_name || '') + (m.contact_email || '')).toLowerCase();
    const inbox = getInboxName(m.inbox_id).toLowerCase();
    return title.includes(q) || message.includes(q) || contact.includes(q) || inbox.includes(q);
  });
});

const fetchMessages = async () => {
  try {
    loading.value = true;
    const res = await AccountScheduledMessagesAPI.get();
    scheduledMessages.value = res.data?.payload || [];
  } catch (err) {
    useAlert(t('SCHEDULE_MESSAGES.ERROR.LOAD'), 'error');
  } finally {
    loading.value = false;
  }
};

const formatDate = ts => {
  return new Date(ts * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getInboxName = inboxId => {
  const inbox = inboxes.value.find(i => i.id === inboxId);
  return inbox ? inbox.name : t('SCHEDULE_MESSAGES.ERROR.INBOX_NOT_FOUND');
};

const getContactDisplayName = msg => {
  const name = msg?.contact_name;
  const email = msg?.contact_email;
  const fallback = t('KANBAN.ITEM_CONVERSATION.NO_CONTACT');
  const text = name || email || fallback;
  return text.length > 25 ? `${text.substring(0, 25)}...` : text;
};

const getStatusText = status => {
  return status === 'sent'
    ? t('SCHEDULE_MESSAGES.STATUS.SENT')
    : t('SCHEDULE_MESSAGES.STATUS.PENDING');
};

const getStatusClass = status => ({
  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300': status === 'pending',
  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': status === 'sent',
});

const getPeriodText = period => {
  const type = typeof period === 'object' && period !== null ? period.type : period;
  const map = {
    every_day: 'EVERY_DAY',
    every_day_at_time: 'EVERY_DAY_AT_TIME',
    every_week: 'EVERY_WEEK',
    every_month: 'EVERY_MONTH',
    every_weekday: 'EVERY_WEEKDAY',
    every_weekend: 'EVERY_WEEKEND',
  };
  return t(`SCHEDULE_MESSAGES.FREQUENCY.${map[type] || 'EVERY_DAY'}`);
};

const toggleExpand = id => {
  const i = expandedIds.value.indexOf(id);
  if (i === -1) expandedIds.value.push(id);
  else expandedIds.value.splice(i, 1);
};

const openDeleteModal = msg => {
  messageToDelete.value = msg;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  messageToDelete.value = null;
};

const closeScheduleModal = () => {
  showScheduleModal.value = false;
  fetchMessages();
};

const confirmDelete = async () => {
  if (!messageToDelete.value?.conversation_display_id) return;
  try {
    await ConversationApi.deleteScheduledMessage(
      messageToDelete.value.conversation_display_id,
      messageToDelete.value.id
    );
    useAlert(t('SCHEDULE_MESSAGES.SUCCESS.DELETE'), 'success');
    await fetchMessages();
  } catch (err) {
    useAlert(t('SCHEDULE_MESSAGES.ERROR.DELETE'), 'error');
  } finally {
    closeDeleteModal();
  }
};

const goToConversation = msg => {
  if (!msg?.conversation_display_id || !accountId.value) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      inboxId: msg.inbox_id,
      conversation_id: msg.conversation_display_id,
    },
  });
};

onMounted(() => {
  fetchMessages();
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-white dark:bg-slate-900 overflow-hidden">
    <!-- Header: mesma estrutura que KanbanHeader (flex justify-between, tudo na mesma linha) -->
    <div
      class="relative z-10 flex flex-nowrap justify-between items-center pt-2 px-4 md:pt-3 md:px-6 md:gap-4 shrink-0"
    >
      <div class="flex items-center gap-1 min-w-0 md:gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-chevron-left cursor-pointer text-slate-600 dark:text-slate-400 shrink-0"
          @click="emit('switchView', 'kanban')"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
        <h2 class="text-base font-medium text-slate-900 dark:text-slate-100 truncate">
          {{ t('SCHEDULE_MESSAGES.LIST.TITLE') }}
        </h2>
        <div
          class="flex items-center relative gap-1"
          :class="{ 'is-active': showSearchInput }"
        >
          <Button variant="ghost" color="slate" size="sm" @click="handleSearch">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.3-4.3"/>
              </svg>
            </template>
          </Button>
          <div
            v-show="showSearchInput || searchQuery"
            class="h-8 flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 overflow-hidden transition-all duration-200 min-w-[200px] focus-within:border-woot-500 focus-within:ring-1 focus-within:ring-woot-500"
          >
            <input
              id="scheduled-messages-search"
              v-model="searchQuery"
              type="search"
              class="w-full h-full bg-transparent border-none outline-none ring-0 m-0 px-3 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-500 placeholder:text-xs"
              :placeholder="t('SCHEDULE_MESSAGES.SEARCH_PLACEHOLDER')"
              @blur="!searchQuery && (showSearchInput = false)"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-1 md:gap-2 flex-nowrap shrink-0">
        <div class="flex items-center gap-2">
          <select
            v-model="inboxFilter"
            class="h-8 m-0 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm min-w-[140px]"
          >
            <option value="all">{{ t('SCHEDULE_MESSAGES.FILTER.ALL_CHANNELS') }}</option>
            <option
              v-for="inbox in inboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <select
            v-model="statusFilter"
            class="h-8 m-0 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm"
          >
            <option value="all">{{ t('SCHEDULE_MESSAGES.FILTER.ALL') }}</option>
            <option value="pending">{{ t('SCHEDULE_MESSAGES.FILTER.PENDING') }}</option>
            <option value="sent">{{ t('SCHEDULE_MESSAGES.FILTER.SENT') }}</option>
          </select>
        </div>
        <div class="flex items-center">
          <Button
            type="button"
            variant="solid"
            color="blue"
            size="sm"
            class="shrink-0 whitespace-nowrap"
            @click="showScheduleModal = true"
          >
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 2v4"/>
                <path d="M16 2v4"/>
                <rect width="18" height="18" x="3" y="4" rx="2"/>
                <path d="M3 10h18"/>
                <path d="M8 14h.01"/>
                <path d="M12 14h.01"/>
                <path d="M16 14h.01"/>
                <path d="M8 18h.01"/>
                <path d="M12 18h.01"/>
                <path d="M16 18h.01"/>
              </svg>
            </template>
            <span class="text-[12px] whitespace-nowrap">{{ t('SCHEDULE_MESSAGES.BUTTON_LABEL') }}</span>
          </Button>
        </div>
      </div>
    </div>

    <ScheduleMessageModal
      :show="showScheduleModal"
      @close="closeScheduleModal"
    />

    <div v-if="loading" class="flex-1 flex items-center justify-center p-8">
      <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-10" />
    </div>

    <div
      v-else-if="filteredMessages.length === 0"
      class="flex-1 flex flex-col items-center justify-center p-8 text-center"
    >
      <span class="i-lucide-calendar-clock size-12 text-n-blue-10 mb-4" />
      <p class="text-sm font-medium text-slate-700 dark:text-slate-300">
        {{ t('SCHEDULE_MESSAGES.EMPTY') }}
      </p>
      <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
        {{ t('SCHEDULE_MESSAGES.EMPTY_SUBTEXT') }}
      </p>
    </div>

    <div v-else class="flex-1 overflow-y-auto p-4 space-y-3">
      <div
        v-for="msg in filteredMessages"
        :key="msg.id"
        class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
      >
        <button
          type="button"
          class="w-full flex items-start justify-between gap-2 p-4 text-left hover:bg-n-solid-3 transition-colors"
          @click="toggleExpand(msg.id)"
        >
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-medium text-slate-900 dark:text-slate-100 truncate">
                {{ msg.title || t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
              </span>
              <span
                class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                :class="getStatusClass(msg.status)"
              >
                {{ getStatusText(msg.status) }}
              </span>
            </div>
            <div class="flex items-center gap-4 mt-1 text-xs text-slate-500 dark:text-slate-400">
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-calendar size-3.5" />
                {{ formatDate(msg.scheduled_at) }}
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-inbox size-3.5" />
                {{ getInboxName(msg.inbox_id) }}
              </span>
              <span
                v-if="msg.is_recurrent"
                class="inline-flex items-center gap-1"
              >
                <span class="i-lucide-repeat size-3.5" />
                {{ getPeriodText(msg.period) }}
              </span>
            </div>
            <!-- Bloco conversa de origem (estilo KanbanItem: display_id + nome contato + inbox) -->
            <div
              v-if="msg.conversation_display_id != null"
              class="flex items-center gap-2 mt-2 px-3.5 py-1.5 rounded-md border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs min-h-[1.5rem] w-full max-w-full cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/80"
              style="box-shadow: 0 4px 24px 0 rgba(30,41,59,0.08)"
              @click.stop="goToConversation(msg)"
            >
              <div class="flex items-center gap-1 shrink-0">
                <span class="i-lucide-message-circle size-3 text-slate-400 dark:text-slate-400" />
                <span class="text-[10px] text-slate-400 dark:text-slate-400 select-all px-0.5">
                  #{{ msg.conversation_display_id }}
                </span>
              </div>
              <div class="flex items-center gap-1 min-w-0 flex-1">
                <span class="truncate font-semibold text-slate-700 dark:text-slate-200">
                  {{ getContactDisplayName(msg) }}
                </span>
                <span class="text-slate-300 dark:text-slate-600 shrink-0">·</span>
                <span class="truncate text-slate-500 dark:text-slate-400 shrink-0 max-w-[120px]">
                  {{ getInboxName(msg.inbox_id) }}
                </span>
              </div>
            </div>
          </div>
          <span
            class="shrink-0 size-5 text-slate-400"
            :class="expandedIds.includes(msg.id) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          />
        </button>
        <div
          v-show="expandedIds.includes(msg.id)"
          class="border-t border-n-weak bg-n-solid-1 p-4 space-y-3"
        >
          <div class="rounded-lg bg-n-solid-2 p-3">
            <p class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {{ t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
            </p>
            <p class="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
              {{ msg.message }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              v-if="msg.conversation_display_id"
              variant="ghost"
              color="slate"
              size="sm"
              @click="goToConversation(msg)"
            >
              <span class="i-lucide-message-circle size-4 mr-1" />
              {{ t('SCHEDULE_MESSAGES.LIST.OPEN_CONVERSATION') }}
            </Button>
            <Button
              variant="ghost"
              color="ruby"
              size="sm"
              @click="openDeleteModal(msg)"
            >
              <span class="i-lucide-trash-2 size-4 mr-1" />
              {{ t('SCHEDULE_MESSAGES.LIST.DELETE') }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <Teleport to="body">
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        @click.self="closeDeleteModal"
      >
        <div
          class="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full p-6 border border-n-weak"
          role="dialog"
          aria-modal="true"
        >
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ t('SCHEDULE_MESSAGES.DELETE_MODAL.TITLE') }}
          </h3>
          <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {{ t('SCHEDULE_MESSAGES.DELETE_MODAL.MESSAGE') }}
          </p>
          <div class="mt-6 flex justify-end gap-2">
            <Button variant="ghost" color="slate" size="sm" @click="closeDeleteModal">
              {{ t('SCHEDULE_MESSAGES.DELETE_MODAL.KEEP') }}
            </Button>
            <Button variant="solid" color="ruby" size="sm" @click="confirmDelete">
              {{ t('SCHEDULE_MESSAGES.DELETE_MODAL.CONFIRM') }}
            </Button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
